/**
 * Staking Page
 * 
 * Full-page staking dashboard for $MANGO token management.
 */

import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StakingDashboard } from "@/components/StakingDashboard";
import { WalletConnect } from "@/components/WalletConnect";
import { useAccount } from "wagmi";
import { Coins, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const Staking = memo(function Staking() {
  const { t } = useTranslation();
  const { isConnected } = useAccount();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow container py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Coins className="h-8 w-8 text-orange-500" />
            $MANGO Staking
          </h1>
          <p className="text-muted-foreground mt-2">
            Stake $MANGO tokens to unlock fee discounts, verified badges, and governance voting
          </p>
        </div>

        {!isConnected ? (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Connect Your Wallet</CardTitle>
                <CardDescription>
                  Connect your wallet to view your staking position and access exclusive benefits
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-6">
                <WalletConnect variant="full" />
                
                <div className="text-center space-y-4 mt-4">
                  <h3 className="font-semibold">Why stake $MANGO?</h3>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li>✓ Up to 75% discount on marketplace fees</li>
                    <li>✓ Verified seller badges</li>
                    <li>✓ Priority listing placement</li>
                    <li>✓ Governance voting rights</li>
                    <li>✓ Revenue share (Diamond tier)</li>
                  </ul>
                </div>

                <div className="flex gap-4 mt-4">
                  <Button variant="outline" asChild>
                    <a 
                      href="https://www.coingecko.com/en/coins/mango" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View on CoinGecko
                    </a>
                  </Button>
                  <Button variant="outline" asChild>
                    <a 
                      href="https://app.uniswap.org" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Buy on Uniswap
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <StakingDashboard />
        )}
      </main>

      <Footer />
    </div>
  );
});

Staking.displayName = "Staking";

export default Staking;
